import React, { useEffect } from 'react';
import { Heading } from '../../../components/Typography';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeReact from 'rehype-react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styles from './SummariesView.module.scss';
import { summaryConfig, orderedSummaryTypes } from '../config';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchSummaries } from '../../../store/research/slice';
import { Company } from '../../../store/company/types';
import EmptyState from './EmptyState';
import Button from '../../../components/UI/Button/Button';
import { openDocument } from '../../../store/document/slice';
import { trackEvent } from '../../../services/tracking';

interface SummariesViewProps {
  company: Company;
}

const renderMarkdown = (markdown: string) => {
  return unified()
    .use(remarkParse)
    .use(remarkRehype)
    .use(rehypeReact, {
      jsx,
      jsxs,
      Fragment,
    })
    .processSync(markdown).result as React.ReactNode;
};

const SummariesView: React.FC<SummariesViewProps> = ({ company }) => {
  const dispatch = useAppDispatch();
  const companySummaries = useAppSelector((state) => state.research.summariesByCompany[company.id]);

  useEffect(() => {
    if (company && company.id) {
      dispatch(fetchSummaries(company));
    }
  }, [company, dispatch]);

  if (!companySummaries || companySummaries.isLoading) {
    return <div className={styles.loadingSpinner}></div>;
  }

  if (companySummaries.error) {
    return <div>Error: {companySummaries.error}</div>;
  }

  const sortedSummaries = Object.values(companySummaries.summaries)
    .filter((summary) => summaryConfig[summary.summary_type])
    .sort((a, b) => {
      const aIndex = orderedSummaryTypes.indexOf(a.summary_type);
      const bIndex = orderedSummaryTypes.indexOf(b.summary_type);
      return aIndex - bIndex;
    });

  if (sortedSummaries.length === 0) {
    return <EmptyState companyName={company.name} />;
  }

  const summaryDate = new Date(sortedSummaries[0].created_at).toLocaleDateString();

  return (
    <div className={styles.summariesView}>
      {sortedSummaries.map((summary) => {
        const config = summaryConfig[summary.summary_type];
        if (!config) return null;

        return (
          <div key={summary.id} className={styles.summarySection}>
            <Heading level={3} className={styles.summaryTitle}>
              {config.heading}
            </Heading>
            <div className={styles.summaryBody}>{renderMarkdown(summary.summary_text)}</div>
          </div>
        );
      })}
      {sortedSummaries.length > 0 &&
        companySummaries.relevantSources &&
        companySummaries.relevantSources.length > 0 && (
          <div className={styles.sourcesSection}>
            <Heading level={3} className={styles.summaryTitle}>
              Sources
            </Heading>
            <div className={styles.summaryBody}>
              Summary generated on {summaryDate} from the following sources:
            </div>
            <ul className={styles.sourcesList}>
              {companySummaries.relevantSources.map((src) => (
                <li key={src.id} className={styles.sourceItem}>
                  <Button
                    variant="outline"
                    size="small"
                    className={styles.sourceLink}
                    onClick={() => {
                      trackEvent('Citation Opened', {
                        location: 'Summaries',
                        documentId: src.id,
                      });
                      dispatch(
                        openDocument({
                          documentId: src.id,
                        }),
                      );
                    }}
                  >
                    {src.name}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};

export default SummariesView;
